import React, { useState, useEffect, useMemo } from "react";
import {
  ComposedChart, LineChart, Line, XAxis, YAxis, Tooltip, Legend, ResponsiveContainer, Brush, Area, ReferenceLine, ReferenceArea
} from "recharts";
import { useTheme } from "../../../../shared/common/ThemeContext";
import { fetchAuth } from '../../../../shared/common/api';
import { motion } from "framer-motion";
import { useSeason } from "../../../../shared/common/SeasonContext";
import PlayerPicker from "../../../../shared/common/PlayerPicker";
import SideControls from "../../../../shared/common/SideControls";
import { useTranslation } from "react-i18next";

const TrainerTeamPerformanceCharts = ({
  predictiveRunId,
  startDate, // "YYYY-MM-DD"
  endDate,   // "YYYY-MM-DD"
  embedded = false
}) => {
    const { t } = useTranslation();
    const [weeklyData, setWeeklyData] = useState([]);
    const [filteredPlayers, setFilteredPlayers] = useState([]);
    const { isDarkMode } = useTheme();
    const [activeTab, setActiveTab] = useState("trainability");

    const todayIso = useMemo(() => new Date().toISOString().slice(0, 10), []);
    const [activePlayers, setActivePlayers] = useState({});
    const { season } = useSeason();
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [displayMode, setDisplayMode] = useState("curve");

    // Match days from calendar
    const [matchDates, setMatchDates] = useState([]);

    useEffect(() => {
      if (!season) return;
      const fetchMatchDates = async () => {
        try {
          const res = await fetchAuth(`/calendar?season=${season}`);
          if (!res.ok) return;
          const cal = await res.json();
          const events = cal.events || {};
          const matches = [];
          for (const [dateKey, dayObj] of Object.entries(events)) {
            for (const period of ["morning", "afternoon"]) {
              const ev = dayObj?.[period];
              if (!ev) continue;
              const evList = Array.isArray(ev) ? ev : [ev];
              for (const e of evList) {
                if (e?.type === "match") {
                  const [y, m, d] = dateKey.split("-");
                  matches.push({
                    date: `${d}/${m}/${y}`,
                    opponent: e.opponent?.name || "",
                  });
                }
              }
            }
          }
          setMatchDates(matches);
        } catch { /* silent */ }
      };
      fetchMatchDates();
    }, [season]);

    const matchDateSet = useMemo(
      () => new Map(matchDates.map(m => [m.date, m.opponent])),
      [matchDates]
    );

    const allMetrics = [
      { key: "trainability", label: "Trainability", unit: "/10" }
    ];

const hasData = useMemo(() => {
  return (filteredPlayers || []).some(p => Array.isArray(p.weeklyHistory) && p.weeklyHistory.length > 0);
}, [filteredPlayers]);

useEffect(() => {
}, [weeklyData]);

    useEffect(() => {
        if (!predictiveRunId) {
            
            return;
        }
        if (!startDate || !endDate) {
            
            return;
        }

        const fetchWeeklyData = async () => {
            try {
            setLoading(true);
            setError(null);

            const qs = new URLSearchParams({
                start_date: startDate,
                end_date: endDate,
                predictive_run_id: predictiveRunId
            });

            const res = await fetchAuth(`/trainer/players_weekly_history_predictive?${qs.toString()}`);
            if (!res.ok) {
                const errorData = await res.json().catch(() => ({}));
                throw new Error(errorData.error || "Failed to fetch data");
            }
            const data = await res.json();
            setWeeklyData(Array.isArray(data) ? data : []);
            } catch (e) {
            
            setError("Unable to load chart data. Please try again later.");
            setWeeklyData([]);
            } finally {
            setLoading(false);
            }
        };

        fetchWeeklyData();
    }, [predictiveRunId, startDate, endDate]);


// Position filtering - removed since we now show all players
    useEffect(() => {
        if (!weeklyData.length) {
            setFilteredPlayers([]);
            setActivePlayers({});
            return;
        }

        // Show all players
        setFilteredPlayers(weeklyData);

        // Init selection: all players active by default
        const initialActivePlayers = {};
        weeklyData.forEach((player) => {
            const name = `${player.first_name} ${player.last_name}`;
            initialActivePlayers[name] = true;
        });

        setActivePlayers(initialActivePlayers);
    }, [weeklyData]);

  const teamRefs = useMemo(() => {
    // VMA max (km/h)
    const vmas = (weeklyData || [])
      .map(p => p?.vma)
      .filter(v => typeof v === "number" && !Number.isNaN(v));
    const teamVmaMax = vmas.length ? Math.max(...vmas) : null;

    // accel max (m/s²) based on "accelerations" values (acceleration_max)
    const accelVals = (weeklyData || []).flatMap(p =>
      (p.weeklyHistory || [])
        .map(s => s?.accelerations)
        .filter(v => typeof v === "number" && !Number.isNaN(v))
    );
    const teamAccelMax = accelVals.length ? Math.max(...accelVals) : null;

    // decel max magnitude (m/s²) based on "decelerations" (negative values)
    const decelVals = (weeklyData || []).flatMap(p =>
      (p.weeklyHistory || [])
        .map(s => s?.decelerations)
        .filter(v => typeof v === "number" && !Number.isNaN(v))
    );
    const teamDecelMagMax = decelVals.length ? Math.max(...decelVals.map(v => Math.abs(v))) : null;

    return {
      teamVmaMax: teamVmaMax ?? 30,         // reasonable fallback
      teamAccelMax: teamAccelMax ?? 6,      // fallback
      teamDecelMagMax: teamDecelMagMax ?? 6 // fallback
    };  }, [weeklyData]);

  const DASHES = ["", "6 4", "3 3", "10 5 2 5"];

  const buildPalette = (count, isDark) => {
    const s = 70;                          // saturation
    const l1 = isDark ? 65 : 45;           // alternate lightness to increase contrast
    const l2 = isDark ? 55 : 55;           // tweak if you want stronger alternation
    return Array.from({ length: count }, (_, i) => {
      const hue = (i * 137.508) % 360;     // golden angle
      const l = i % 2 ? l1 : l2;
      return `hsl(${hue}, ${s}%, ${l}%)`;
    });
  };

  const useSeriesStyles = (needed, isDark) => {
    return useMemo(() => {
      const colors = buildPalette(Math.min(needed, 48), isDark); // 48 well-separated hues
      return { colors, dashes: DASHES };
    }, [needed, isDark]);
  };

  const playersOrdered = useMemo(
    () => filteredPlayers.map(p => `${p.first_name} ${p.last_name}`).sort((a,b) => a.localeCompare(b)),
    [filteredPlayers]
  );

  const pickerPlayers = useMemo(
    () =>
      filteredPlayers.map((p) => ({
        name: `${p.first_name} ${p.last_name}`,
        position: p.position || "N/A",
      })),
    [filteredPlayers]
  );

  const { colors, dashes } = useSeriesStyles(48, isDarkMode); // 48 colors * 2+ dashes = 96+ unique styles

  const getStyleForPlayer = (name) => {
    const i = playersOrdered.indexOf(name);
    const color = colors[i % colors.length];
    const dash = dashes[Math.floor(i / colors.length) % dashes.length];
    return { color, dash };
  };

  const clampRange = (min, max, a, b) => {
    // retourne [lo, hi] borné et trié
    let lo = Math.max(min, Math.min(a, b));
    let hi = Math.min(max, Math.max(a, b));
    return [lo, hi];
  };

  const processChartData = (metric) => {
    let chartData = [];

    const activeFilteredPlayers = filteredPlayers.filter((p) => {
      const name = `${p.first_name} ${p.last_name}`;
      return activePlayers[name];
    });

    // Fallback : si personne n'est actif (cas extrême), on retombe sur tous les filtrés
    const basePlayersForStats = activeFilteredPlayers.length
      ? activeFilteredPlayers
      : filteredPlayers;


    // 1) Dates uniques (avec données)
    const datesWithData = [...new Set(
      filteredPlayers.flatMap(player =>
        (player.weeklyHistory || []).map(session => session.day)
      )
    )].sort((a, b) => {
      const dateA = new Date(a.split("/").reverse().join("-"));
      const dateB = new Date(b.split("/").reverse().join("-"));
      return dateA - dateB;
    });

    // 2) Remplir TOUS les jours entre la première et la dernière date
    const allDates = (() => {
      if (datesWithData.length < 2) return datesWithData;
      const parse = (d) => { const [dd, mm, yyyy] = d.split("/"); return new Date(yyyy, mm - 1, dd); };
      const fmt = (d) => `${String(d.getDate()).padStart(2,"0")}/${String(d.getMonth()+1).padStart(2,"0")}/${d.getFullYear()}`;
      const start = parse(datesWithData[0]);
      const end = parse(datesWithData[datesWithData.length - 1]);
      const dates = [];
      const cur = new Date(start);
      while (cur <= end) { dates.push(fmt(cur)); cur.setDate(cur.getDate() + 1); }
      return dates;
    })();

    allDates.forEach(date => {
      const dataPoint = { date };

      // 🔹 Déterminer si ce point est prédictif
      const isPredictive = filteredPlayers.some((player) => {
        const session = (player.weeklyHistory || []).find(s => s.day === date);
        return session?.phase === "predictive";
      });
      dataPoint._isPredictive = isPredictive;

      filteredPlayers.forEach((player) => {
        const session = (player.weeklyHistory || []).find(s => s.day === date);
        if (!session) return;

        if (["acceleration_threshold", "deceleration_threshold", "sprint_threshold"].includes(metric)) {
          const accelArray = session.acceleration || [];
          const velArray   = session.velocity || [];

          const keyFor = (action) => {
            if (mode === "number")   return `${action}_number`;
            if (mode === "time")     return `${action}_duration`;
            return `${action}_distance`;
          };

          const fullName = `${player.first_name} ${player.last_name}`;
          let val = null;

          if (metric === "acceleration_threshold") {
            const ref = teamRefs.teamAccelMax; // m/s²

            const [minPct, maxPct] = clampRange(0, 130, accelPctRange[0], accelPctRange[1]);
            const minT = (minPct / 100) * ref;
            const maxT = (maxPct / 100) * ref;

            const iLo = accelArray.findIndex(a => a >= minT);
            const iHi = accelArray.findIndex(a => a >= maxT);

            if (iLo !== -1) {
              const arr   = session[keyFor("acceleration")] || [];
              const loVal = arr[iLo] ?? null;
              const hiVal = (iHi !== -1 ? arr[iHi] : null);
              if (loVal != null) {
                val = (hiVal != null ? (loVal - hiVal) : loVal);
                val = typeof val === "number" ? parseFloat(val.toFixed(2)) : null;
              }
            }
          }


          if (metric === "deceleration_threshold") {
            const refMag = teamRefs.teamDecelMagMax; // m/s² (magnitude)

            const [loPct, hiPct] = clampRange(0, 130, decelPctRange[0], decelPctRange[1]);

            const loMag = (loPct / 100) * refMag; // lower threshold (magnitude)
            const hiMag = (hiPct / 100) * refMag; // upper threshold (magnitude)

            // negative thresholds
            const maxNeg = -loMag; // ex: -2
            const minNeg = -hiMag; // ex: -6

            const minMag = -minNeg; // hiMag
            const maxMag = -maxNeg; // loMag

            const iLo = accelArray.findIndex(a => a >= maxMag); // >= loMag
            const iHi = accelArray.findIndex(a => a >= minMag); // >= hiMag

            if (iLo !== -1) {
              const arr   = session[keyFor("deceleration")] || [];
              const loVal = arr[iLo] ?? null;
              const hiVal = (iHi !== -1 ? arr[iHi] : null);
              if (loVal != null) {
                val = (hiVal != null ? (loVal - hiVal) : loVal);
                val = typeof val === "number" ? parseFloat(val.toFixed(2)) : null;
              }
            }
          }


          if (metric === "sprint_threshold") {
            const vmaMax = teamRefs.teamVmaMax; // km/h

            // range in %
            const [minPct, maxPct] = clampRange(0, 130, sprintPctRange[0], sprintPctRange[1]); // allows >100%
            const minKmH = (minPct / 100) * vmaMax;
            const maxKmH = (maxPct / 100) * vmaMax;

            const minMs = minKmH / 3.6;
            const maxMs = maxKmH / 3.6;

            const iLo = velArray.findIndex(v => v >= minMs);
            const iHi = velArray.findIndex(v => v >= maxMs);

            if (iLo !== -1) {
              const arr   = session[keyFor("sprint")] || [];
              const loVal = arr[iLo] ?? null;
              const hiVal = (iHi !== -1 ? arr[iHi] : null);
              if (loVal != null) {
                val = (hiVal != null ? (loVal - hiVal) : loVal);
                val = typeof val === "number" ? parseFloat(val.toFixed(2)) : null;
              }
            }
          }


          dataPoint[fullName] = val;
        } else {
            const value = session ? session[metric] : null;
            const fullName = `${player.first_name} ${player.last_name}`;

            if (metric === "trainability") {
              const isPred = session?.phase === "predictive";
              dataPoint[`${fullName}__real`] = isPred ? null : value;
              dataPoint[`${fullName}__pred`] = isPred ? value : null;
            } else {
              dataPoint[fullName] = value;
            }

        }
      });

      chartData.push(dataPoint);
    });

    // Cumulative if needed
    if (["intensity_index", "mechanical_load_index"].includes(metric) && cumulative) {
      const cumulativeSums = {};
      chartData = chartData.map((point) => {
        const newPoint = { date: point.date };
        filteredPlayers.forEach((player) => {
          const name = `${player.first_name} ${player.last_name}`;
          const current = typeof point[name] === "number" ? point[name] : 0;
          if (!cumulativeSums[name]) cumulativeSums[name] = 0;
          cumulativeSums[name] += current;
          newPoint[name] = parseFloat(cumulativeSums[name].toFixed(1));
        });
        return newPoint;
      });
    }

    // Median / standard deviation / out-of-band flags
    chartData = chartData.map((point) => {
      const values = basePlayersForStats
        .map((p) => {
          const name = `${p.first_name} ${p.last_name}`;
          let v = point[name];
          if (metric === "trainability") {
            v = (point[`${name}__pred`] ?? point[`${name}__real`]);
          }
          return typeof v === "number" && !Number.isNaN(v) ? v : null;

        })
        .filter((v) => v != null);

      if (!values.length) return point;

      const sortedVals = [...values].sort((a, b) => a - b);
      const mid = Math.floor(sortedVals.length / 2);
      const median = sortedVals.length % 2
        ? sortedVals[mid]
        : (sortedVals[mid - 1] + sortedVals[mid]) / 2;

      const variance = values.reduce((acc, v) => acc + (v - median) ** 2, 0) / values.length;
      const std = Math.sqrt(variance);

      const bandLow  = median - std;
      const bandHigh = median + std;
      const bandRange = bandHigh - bandLow;

      const rawSpan = bandHigh - bandLow || 1;
      const epsilon = rawSpan * 0.02;

      const effectiveLow  = bandLow  - epsilon;
      const effectiveHigh = bandHigh + epsilon;

      const newPoint = {
        ...point,
        median,
        bandLow,
        bandHigh,
        bandBase: bandLow,
        bandRange: bandRange,
      };

      filteredPlayers.forEach((p) => {
        const name = `${p.first_name} ${p.last_name}`;
        let v = point[name];
        if (metric === "trainability") {
          v = (point[`${name}__pred`] ?? point[`${name}__real`]);
        }
        const isOut =
          typeof v === "number" &&
          !Number.isNaN(v) &&
          (v < effectiveLow || v > effectiveHigh);

        newPoint[`${name}__isOut`]   = isOut;
        newPoint[`${name}__outDot`]  = null;  // on remplira après
      });

      return newPoint;
    });

    // 🔹 Build colored segments + out-of-band dots
    const coloredKeysByPlayer = {};
  const getV = (point, name) => {
    if (metric === "trainability") {
      return point[`${name}__pred`] ?? point[`${name}__real`];
    }
    return point[name];
  };
    filteredPlayers.forEach((p) => {
      const name = `${p.first_name} ${p.last_name}`;
      coloredKeysByPlayer[name] = [];
      let segmentIndex = 0;
      let currentKey = null;
      let inSegment = false;

      for (let i = 0; i < chartData.length; i++) {
        const point = chartData[i];
        const v = getV(point, name);
        const currIsValid = typeof v === "number" && !Number.isNaN(v);
        const currIsOut = !!point[`${name}__isOut`];

      if (currIsValid && currIsOut) {
          point[`${name}__outDot`] = v; 
        }

        if (i === 0) continue;

        const prevPoint = chartData[i - 1];
        const prevV = getV(prevPoint, name);
        const prevIsValid = typeof prevV === "number" && !Number.isNaN(prevV);
        const prevIsOut = !!prevPoint[`${name}__isOut`];

        // Règle : colorer le segment si au moins un des 2 points est out-of-band
        const shouldColorEdge = prevIsValid && currIsValid && (prevIsOut || currIsOut);

        if (shouldColorEdge) {
          if (!inSegment) {
            currentKey = `${name}__out_${segmentIndex++}`;
            coloredKeysByPlayer[name].push(currentKey);
            inSegment = true;
          }
          // On pose les valeurs pour ce segment uniquement sur cette clé
          if (prevPoint[currentKey] == null) prevPoint[currentKey] = prevV;
          if (point[currentKey] == null) point[currentKey] = v;
        } else {
          inSegment = false;
          currentKey = null;
        }
      }
    });

// Y domain
const numericValues = chartData
  .flatMap((point) =>
    basePlayersForStats.map((p) => {
      const k = `${p.first_name} ${p.last_name}`;
      if (metric === "trainability") {
        return point[`${k}__pred`] ?? point[`${k}__real`];
      }
      return point[k];
    })
  )
  .filter((v) => typeof v === "number" && !Number.isNaN(v)); 

const minRaw = numericValues.length ? Math.min(...numericValues) : 0;
const maxRaw = numericValues.length ? Math.max(...numericValues) : 1;


    const minValue = Math.round(minRaw * 0.95);
    const maxValue = Math.round(maxRaw * 1.05);

    return {
      chartData,
      players: filteredPlayers.map(p => `${p.first_name} ${p.last_name}`),
      minValue,
      maxValue,
      coloredKeysByPlayer,
    };
  };


  return (
    <div className={`p-6 min-h-screen ${isDarkMode ? "bg-gray-900 text-white" : "bg-gray-100 text-black"}`}>
      {/* Display mode selector */}
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3 mb-4">
        <div className="flex flex-col sm:flex-row sm:items-center gap-3">

          {/* Toggle points / curves */}
          <div className="flex items-center gap-2">
            <span className="text-sm opacity-80">Display:</span>
            <div
              className={`inline-flex text-xs rounded-lg border overflow-hidden ${
                isDarkMode
                  ? "border-gray-700 bg-gray-900"
                  : "border-gray-300 bg-white"
              }`}
            >
              <button
                type="button"
                onClick={() => setDisplayMode("points")}
                className={`px-3 py-1.5 transition ${
                  displayMode === "points"
                    ? "bg-indigo-600 text-white"
                    : isDarkMode
                      ? "text-gray-200 hover:bg-gray-800"
                      : "text-gray-700 hover:bg-gray-100"
                }`}
              >
                {t('charts.points')}
              </button>
              <button
                type="button"
                onClick={() => setDisplayMode("curve")}
                className={`px-3 py-1.5 border-l ${
                  isDarkMode ? "border-gray-700" : "border-gray-300"
                } transition ${
                  displayMode === "curve"
                    ? "bg-indigo-600 text-white"
                    : isDarkMode
                      ? "text-gray-200 hover:bg-gray-800"
                      : "text-gray-700 hover:bg-gray-100"
                }`}
              >
                Curves
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* ---------------------- CENTRAL CONTENT ------------------------ */}
      {loading && (
        <div className="flex flex-col items-center justify-center py-12">
          <div className={`animate-spin rounded-full h-12 w-12 border-b-2 mb-4 ${
            isDarkMode ? "border-indigo-400" : "border-indigo-600"
          }`}></div>
          <p className="text-sm opacity-70">Loading chart data...</p>
        </div>
      )}

      {!loading && error && (
        <div className={`flex flex-col items-center justify-center py-12 px-4 rounded-lg ${
          isDarkMode ? "bg-red-900/20 border border-red-800" : "bg-red-50 border border-red-200"
        }`}>
          <svg className="w-12 h-12 mb-3 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
          </svg>
          <p className={`text-sm font-medium mb-1 ${isDarkMode ? "text-red-400" : "text-red-600"}`}>
            Error loading data
          </p>
          <p className="text-xs opacity-70 text-center">
            {error}
          </p>
        </div>
      )}

      {!loading && !error && !hasData && (
        <div className="flex flex-col items-center justify-center py-12">
          <svg className="w-12 h-12 mb-3 opacity-30" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
          </svg>
          <p className="text-sm opacity-70 text-center">
            No data available for this period.
          </p>
        </div>
      )}

      {!loading && !error && hasData && (
        <>
          {allMetrics.map(({ key, label, unit }) => {
            const { chartData, players, minValue, maxValue, coloredKeysByPlayer } = processChartData(key);
            const predictiveBoundaryDate = chartData.find((point) => point._isPredictive)?.date || null;
            const lastDate = chartData.length ? chartData[chartData.length - 1].date : null;

            return (
              <div key={key} className="mb-8">
                <div className="flex flex-col xl:flex-row items-stretch gap-4">
                  {/* === Graph: takes all remaining space === */}
                  <div className="flex-1 min-w-0">
                    <ResponsiveContainer width="100%" height={420}>
                      <ComposedChart
                        data={chartData}
                        margin={{ top: 10, right: 20, left: 0, bottom: 30 }}
                      >
                        <XAxis
                          dataKey="date"
                          angle={-45}
                          textAnchor="end"
                          height={70}
                          interval={0}
                          tickFormatter={(value) => {
                            const [day, month] = value.split("/").slice(0, 2);
                            return `${day}/${month}`;
                          }}
                        />
                        <YAxis
                          domain={[minValue, maxValue]}
                          tickFormatter={(v) => (v != null ? v.toFixed(1) : "")}
                        />
                        {/* 🔹 Zone de fond pour la partie prédictive */}
                        {predictiveBoundaryDate && lastDate && (
                          <ReferenceArea
                            x1={predictiveBoundaryDate}
                            x2={lastDate}
                            fill={isDarkMode ? "rgba(251,191,36,0.08)" : "rgba(245,158,11,0.08)"}
                            fillOpacity={0.5}
                            ifOverflow="extendDomain"
                          />
                        )}
                        {/* 🔹 Ligne verticale de séparation */}
                        {predictiveBoundaryDate && (
                            <ReferenceLine
                                x={predictiveBoundaryDate}
                                stroke={isDarkMode ? "rgba(251,191,36,0.9)" : "rgba(245,158,11,0.9)"}
                                strokeDasharray="6 6"
                                ifOverflow="extendDomain"
                                label={{
                                value: "Predictive",
                                position: "insideTopRight",
                                fill: isDarkMode ? "rgba(251,191,36,0.95)" : "rgba(180,83,9,0.95)",
                                fontSize: 12,
                                fontWeight: 600
                                }}
                            />
                        )}

                        {/* Match day indicators */}
                        {chartData
                          .filter(d => matchDateSet.has(d.date))
                          .map(d => (
                            <ReferenceLine
                              key={`match-${d.date}`}
                              x={d.date}
                              stroke={isDarkMode ? "rgba(251,191,36,0.5)" : "rgba(217,119,6,0.4)"}
                              strokeDasharray="4 3"
                              strokeWidth={1.4}
                              ifOverflow="extendDomain"
                              label={{
                                value: "▼",
                                position: "top",
                                fontSize: 11,
                                offset: 2,
                                fill: isDarkMode ? "#fbbf24" : "#d97706",
                              }}
                            />
                          ))
                        }

                        <Tooltip
                          content={({ active, payload, label }) => {
                            if (!active || !payload?.length) return null;
                            
                            // 🔹 Déterminer si ce point est prédictif
                            const point = chartData.find(d => d.date === label);
                            const isPredictive = point?._isPredictive;
                            
                            let periodLabel = "";
                            for (let player of filteredPlayers) {
                              const session = (player.weeklyHistory || []).find(s => s.day === label);
                              if (session) { periodLabel = session.period; break; }
                            }
                            const matchOpponent = matchDateSet.get(label);
                            return (
                              <div className="bg-white dark:bg-gray-800 text-gray-800 dark:text-white p-3 border border-gray-300 dark:border-gray-600 rounded shadow-md">
                                <div className="flex items-center justify-between mb-2">
                                  <p className="font-semibold text-sm">{label} {periodLabel ? `- ${periodLabel}` : ""}</p>
                                  {isPredictive && (
                                    <span className="ml-2 px-2 py-0.5 text-xs rounded bg-amber-500 text-white font-medium">
                                      Predictive
                                    </span>
                                  )}
                                </div>
                                {matchOpponent !== undefined && (
                                  <div className="flex items-center gap-1.5 text-xs mb-2 px-1.5 py-1 rounded bg-amber-500/10 text-amber-600 dark:text-amber-400 font-medium">
                                    <span>▼</span>
                                    <span>{t('charts.match_day')}{matchOpponent ? ` — ${matchOpponent}` : ""}</span>
                                  </div>
                                )}
                                {payload.map((entry, idx) => (
                                  <div key={idx} className="flex items-center text-sm mb-1">
                                    <span className="inline-block w-3 h-3 rounded-full mr-2" style={{ backgroundColor: entry.color }} />
                                    <span className="mr-2">{entry.name}:</span>
                                    <span className="font-medium">{entry.value}</span>
                                  </div>
                                ))}
                              </div>
                            );
                          }}
                        />
                        <Legend wrapperStyle={{ display: "none" }} className="hidden md:block" />

                        <XAxis
                          dataKey="date"
                          angle={0}
                          tickMargin={8}
                          height={40}
                          interval={0}
                          tickFormatter={(value) => {
                            const [day, month] = value.split("/").slice(0, 2);
                            return `${day}/${month}`;
                          }}
                        />

                        {/* 🔍 ZOOM / DÉZOOM SUR L’AXE X */}
                        <Brush
                          dataKey="date"
                          height={32}
                          travellerWidth={12}
                          stroke={isDarkMode ? "#4f46e5" : "#6366f1"} // indigo-600-ish
                          fill={isDarkMode ? "rgba(79,70,229,0.15)" : "rgba(129,140,248,0.16)"} // indigo/indigo-400 soft
                        >
                          {/* Mini-preview of median to recall the main graph */}
                          <LineChart data={chartData}>
                            <Line
                              type="monotone"
                              dataKey="median"
                              stroke={isDarkMode ? "#e5e7eb" : "#111827"} // gris clair / presque noir
                              strokeWidth={1.3}
                              dot={false}
                              isAnimationActive={false}
                            />
                          </LineChart>
                        </Brush>

                        <Line
                          type="monotone"
                          dataKey="median"
                          stroke={isDarkMode ? "#ffffff" : "#000000"}
                          strokeWidth={2}
                          dot={false}
                          isAnimationActive={false}
                          name="Team median"
                        />
                        {displayMode === "points" && (
                          <>
                            <Line
                              type="monotone"
                              dataKey="bandLow"
                              stroke={
                                isDarkMode
                                  ? "rgba(148,163,184,0.9)"
                                  : "rgba(148,163,184,0.9)"
                              }
                              strokeWidth={1}
                              strokeDasharray="4 4"
                              dot={false}
                              isAnimationActive={false}
                              name="-1σ"
                            />
                            <Line
                              type="monotone"
                              dataKey="bandHigh"
                              stroke={
                                isDarkMode
                                  ? "rgba(148,163,184,0.9)"
                                  : "rgba(148,163,184,0.9)"
                              }
                              strokeWidth={1}
                              strokeDasharray="4 4"
                              dot={false}
                              isAnimationActive={false}
                              name="+1σ"
                            />

                            {/* Filled area = standard deviation range */}
                            <Area
                              type="monotone"
                              dataKey="bandBase"
                              stackId="band"
                              stroke="none"
                              fill="transparent"
                              isAnimationActive={false}
                            />
                            <Area
                              type="monotone"
                              dataKey="bandRange"
                              stackId="band"
                              stroke="none"
                              fill={
                                isDarkMode
                                  ? "rgba(129,140,248,0.35)" // indigo-400-ish
                                  : "rgba(129,140,248,0.25)"
                              }
                              isAnimationActive={false}
                            />
                          </>
                        )}


                        {/* Player lines: part IN range = gray, out of range = colored */}
                        {/* Curve mode: current behavior, with colored segments */}
                        {displayMode === "curve" &&
  players.filter((player) => activePlayers[player]).map((player) => {
    const { color, dash } = getStyleForPlayer(player);

    if (activeTab === "trainability") {
      return (
        <React.Fragment key={player}>
          {/* past */}
          <Line
            type="monotone"
            dataKey={`${player}__real`}
            stroke={color}
            strokeWidth={1.8}
            strokeDasharray={dash}
            dot={{ r: 2.5, stroke: color, fill: isDarkMode ? "#111827" : "#ffffff" }}
            activeDot={{ r: 4 }}
            connectNulls={false}
            isAnimationActive={false}
            name={player}
          />
          {/* predicted */}
          <Line
            type="monotone"
            dataKey={`${player}__pred`}
            stroke={color}
            strokeWidth={2.2}
            strokeDasharray="6 4"          // ✅ clearly “future”
            dot={{ r: 2.5, stroke: color, fill: isDarkMode ? "#111827" : "#ffffff" }}
            activeDot={{ r: 4 }}
            connectNulls={false}
            isAnimationActive={false}
            name={`${player} (pred)`}
          />
        </React.Fragment>
      );
    }

    return (
      <Line
        key={player}
        type="monotone"
        dataKey={player}
        stroke={color}
        strokeWidth={1.8}
        strokeDasharray={dash}
        dot={{ r: 2.5, stroke: color, fill: isDarkMode ? "#111827" : "#ffffff" }}
        activeDot={{ r: 4 }}
        connectNulls={false}
        isAnimationActive={false}
        name={player}
      />
    );
  })}


                        {/* Points mode: only out-of-band points, no curves */}
                        {displayMode === "points" &&
                          players
                            .filter((player) => activePlayers[player])
                            .map((player) => {
                              const { color } = getStyleForPlayer(player);
                              return (
                                <Line
                                  key={`${player}__points`}
                                  type="monotone"
                                  dataKey={`${player}__outDot`}
                                  stroke="none"
                                  dot={{
                                    r: 3,
                                    stroke: color,
                                    fill: isDarkMode ? "#111827" : "#ffffff",
                                  }}
                                  activeDot={{ r: 5 }}
                                  isAnimationActive={false}
                                  name={player}
                                />
                              );
                            })}

                      </ComposedChart>
                    </ResponsiveContainer>
                  </div>

                  {/* === Player Picker Sidebar === */}
                  <SideControls
                    showSliders={false}
                    picker={
                      <PlayerPicker
                        compact
                        defaultPosition="all"
                        players={pickerPlayers}
                        activeMap={activePlayers}
                        onToggle={(name) =>
                          setActivePlayers((prev) => ({ ...prev, [name]: !prev[name] }))
                        }
                        onAll={() =>
                          setActivePlayers(
                            Object.fromEntries(pickerPlayers.map((p) => [p.name, true]))
                          )
                        }
                        onNone={() =>
                          setActivePlayers(
                            Object.fromEntries(pickerPlayers.map((p) => [p.name, false]))
                          )
                        }
                        onInvert={() =>
                          setActivePlayers(
                            Object.fromEntries(
                              pickerPlayers.map((p) => [p.name, !activePlayers[p.name]])
                            )
                          )
                        }
                        getStyleForPlayer={getStyleForPlayer}
                        isDark={isDarkMode}
                      />
                    }
                    sliders={null}
                    isDarkMode={isDarkMode}
                  />
                </div>
              </div>

            );
          })}
        </>
      )}
    </div>
  );
};

export default TrainerTeamPerformanceCharts;
